/**
 * Option « Ruban personnalisé » de la fiche produit.
 *
 * Le bloc se place sous le choix de la taille, donc en dehors du formulaire
 * d'achat. Ses champs n'ont de toute façon pas d'attribut `name` : rien ne part
 * avec la ligne de la composition, tout est lu au moment de l'envoi.
 *
 * Au moment de l'ajout au panier, si l'option est cochée :
 *   1. la ligne « ruban » est ajoutée en silence (pas de sections demandées,
 *      donc aucun rafraîchissement du tiroir) ;
 *   2. l'ajout normal du thème reprend la main et rafraîchit le tiroir une
 *      seule fois, avec les deux lignes.
 *
 * Les écouteurs sont posés sur `document` en phase de capture : le thème
 * branche les siens sur `document` en phase de bulle, donc les nôtres passent
 * toujours en premier et peuvent interrompre la propagation. C'est aussi ce qui
 * permet de viser le formulaire sans en être un descendant.
 */
class FddRibbon extends HTMLElement {
  /** @type {HTMLFormElement | null} */
  #form = null;

  /** @type {(HTMLElement & { handleSubmit?: (event: Event) => void }) | null} */
  #productForm = null;

  /** @type {HTMLInputElement | null} */
  #toggle = null;

  /** @type {HTMLElement | null} */
  #panel = null;

  /** @type {HTMLInputElement | null} */
  #message = null;

  /** @type {HTMLElement | null} */
  #error = null;

  connectedCallback() {
    this.#toggle = this.querySelector('[data-ribbon-toggle]');
    this.#panel = this.querySelector('[data-ribbon-panel]');
    this.#message = this.querySelector('[data-ribbon-message]');
    this.#error = this.querySelector('[data-ribbon-error]');

    if (!this.#toggle || !this.#panel || !this.#message) return;

    this.#toggle.addEventListener('change', this.#onToggle);
    this.#message.addEventListener('input', this.#onInput);
    document.addEventListener('click', this.#onClick, true);
    document.addEventListener('submit', this.#onSubmit, true);

    // Le navigateur peut restaurer l'état de la case au retour arrière.
    this.#syncPanel();
  }

  disconnectedCallback() {
    this.#toggle?.removeEventListener('change', this.#onToggle);
    this.#message?.removeEventListener('input', this.#onInput);
    document.removeEventListener('click', this.#onClick, true);
    document.removeEventListener('submit', this.#onSubmit, true);
  }

  /**
   * Le formulaire d'achat est cherché à la demande : au moment où le bloc est
   * initialisé, il n'est pas encore analysé par le navigateur. Le thème le
   * remplace aussi au changement de variante, d'où la vérification.
   * @returns {HTMLFormElement | null}
   */
  #getForm() {
    if (this.#form?.isConnected) return this.#form;

    const root = this.closest('.shopify-section') ?? document;

    this.#productForm = root.querySelector('product-form-component');
    this.#form =
      this.#productForm?.querySelector('form[data-type="add-to-cart-form"]') ??
      this.#productForm?.querySelector('form') ??
      null;

    return this.#form;
  }

  #onToggle = () => {
    this.#syncPanel();

    if (this.#isEnabled()) {
      this.#message?.focus();
    } else {
      this.#clearError();
    }
  };

  #syncPanel() {
    const enabled = this.#isEnabled();

    if (this.#panel) this.#panel.hidden = !enabled;
    this.#toggle?.setAttribute('aria-expanded', enabled ? 'true' : 'false');
  }

  #onInput = () => {
    if (this.#getMessage()) this.#clearError();
  };

  /**
   * Intercepte le clic sur le bouton d'ajout avant l'animation du thème,
   * pour ne pas laisser croire que la composition part au panier.
   * @param {Event} event
   */
  #onClick = (event) => {
    const target = event.target;

    if (!(target instanceof Element)) return;

    const button = target.closest('button[name="add"]');

    if (!button || button.closest('form') !== this.#getForm()) return;
    if (!this.#isEnabled() || this.#getMessage()) return;

    event.preventDefault();
    event.stopPropagation();
    this.#rejectEmptyMessage();
  };

  /** @param {Event} event */
  #onSubmit = (event) => {
    if (event.target !== this.#getForm()) return;
    if (!this.#isEnabled()) return;

    event.preventDefault();
    event.stopPropagation();

    const message = this.#getMessage();

    if (!message) {
      this.#rejectEmptyMessage();
      return;
    }

    this.#addRibbonThenSubmit(message, event);
  };

  /**
   * @param {string} message
   * @param {Event} event
   */
  async #addRibbonThenSubmit(message, event) {
    const variantId = Number(this.dataset.variantId);

    if (Number.isFinite(variantId) && variantId > 0) {
      const addUrl = globalThis.Theme?.routes?.cart_add_url || '/cart/add';

      try {
        const response = await fetch(addUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            items: [
              {
                id: variantId,
                quantity: this.#getQuantity(),
                properties: {
                  Message: message,
                  Pour: this.dataset.productTitle || '',
                },
              },
            ],
          }),
        });

        const payload = await response.json();

        if (!response.ok || payload.status) {
          throw new Error(payload.description || payload.message || 'Ajout du ruban impossible');
        }
      } catch (error) {
        console.error(error);
        this.#showError(this.dataset.errorAdd);
        return;
      }
    }

    this.#clearError();

    // On rend la main au thème : c'est lui qui ajoute la composition,
    // rafraîchit le tiroir et émet ses évènements.
    if (typeof this.#productForm?.handleSubmit === 'function') {
      this.#productForm.handleSubmit(event);
    } else {
      this.#getForm()?.submit();
    }
  }

  #rejectEmptyMessage() {
    this.#showError(this.dataset.errorEmpty);
    this.#message?.setAttribute('aria-invalid', 'true');
    this.#message?.scrollIntoView({ block: 'center', behavior: 'smooth' });
    this.#message?.focus({ preventScroll: true });
  }

  /** @param {string | undefined} text */
  #showError(text) {
    if (!this.#error || !text) return;

    this.#error.textContent = text;
    this.#error.hidden = false;
  }

  #clearError() {
    if (this.#error) {
      this.#error.textContent = '';
      this.#error.hidden = true;
    }

    this.#message?.removeAttribute('aria-invalid');
  }

  #isEnabled() {
    return Boolean(this.#toggle?.checked);
  }

  #getMessage() {
    return this.#message?.value.trim() || '';
  }

  #getQuantity() {
    const input = this.#getForm()?.querySelector('[name="quantity"]');
    const quantity = input instanceof HTMLInputElement ? Number(input.value) : NaN;

    return Number.isFinite(quantity) && quantity > 0 ? quantity : 1;
  }
}

if (!customElements.get('fdd-ribbon')) {
  customElements.define('fdd-ribbon', FddRibbon);
}
